
import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BookOpen, FileText, Book, Brain } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export const ScriptureTools = () => {
  const headingRef = useRef(null);
  const subtextRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: headingRef,
    offset: ["start end", "center center"]
  });

  const subtextScrollYProgress = useScroll({
    target: subtextRef,
    offset: ["start end", "center center"]
  }).scrollYProgress;

  const scale = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const subtextScale = useTransform(subtextScrollYProgress, [0, 1], [0, 1]);

  return (
    <div className="text-center pb-16">
      <motion.h2 
        ref={headingRef}
        className="font-playfair mb-6 text-3xl sm:text-4xl md:text-7xl whitespace-nowrap overflow-hidden"
        style={{ scale }}
      >
        <span className="text-[#1a1f2c]">SCRIPTURE</span>{" "}
        <span className="text-heaven-gold">TOOLS</span>
      </motion.h2>

      <motion.p 
        ref={subtextRef}
        className="text-[#6b7280] max-w-3xl mx-auto text-lg mb-16"
        style={{ scale: subtextScale }}
      >
        Discover powerful AI-powered tools designed to deepen your 
        understanding of God's Word and strengthen your faith journey.
      </motion.p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
        {[
          {
            icon: BookOpen,
            title: "Verse Finder",
            description: "Receive personalized Scripture recommendations based on your current situation or spiritual needs.",
            link: "/find-your-verse"
          },
          {
            icon: Book,
            title: "Scripture Navigator",
            description: "Explore the Bible by topics, themes, and keywords to deepen your understanding of God's Word.",
            link: "/scripture-navigator"
          },
          {
            icon: Brain,
            title: "Deep Dive Commentary",
            description: "Explore profound theological insights and historical context for any passage of Scripture.",
            link: "/deep-dive"
          },
          {
            icon: FileText,
            title: "Etymology Study",
            description: "Uncover the rich meaning of biblical words through their original Hebrew and Greek roots.",
            link: "/etymology"
          }
        ].map((tool, index) => (
          <motion.div 
            key={tool.title}
            initial={{
              opacity: 0,
              x: -50
            }}
            whileInView={{
              opacity: 1,
              x: 0
            }}
            transition={{
              duration: 0.5,
              delay: index * 0.2
            }}
            viewport={{
              once: true
            }}
            className="text-center"
          >
            <div className="flex justify-center mb-4">
              <tool.icon className="w-12 h-12 text-heaven-gold" />
            </div>
            <h3 className="text-[#4285f4] text-xl font-medium mb-3">{tool.title}</h3>
            <p className="text-[#6b7280] text-sm mb-4">
              {tool.description}
            </p>
            <Button 
              asChild 
              variant="blue"
              className="text-white hover:bg-[#0FA0CE]"
            >
              <Link to={tool.link}>
                Access Tool
              </Link>
            </Button>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ScriptureTools;
